"use client";
import { oneDarker } from "@areahub/ui";
import { Box, Flex, Heading, Text } from "@chakra-ui/react";
import { PageBlockPodcastHomePlayer } from "lib/gcms";
import Image from "next/image";
import { SectionTemplate } from "../section-template";
import { AllEpisodesButton } from "./all-episodes-button";
import { AudioButton } from "./audio-button";

export function CustomPodcastPlayer({
  header,
  title,
  subtitle,
  color,
  bg,
}: PageBlockPodcastHomePlayer) {
  const data = {
    platforms: [
      {
        name: "Apple Podcasts",
        src: "/static/podcast/apple.webp",
        link: "https://podcasts.apple.com/us/podcast/climate-clear/id1591105259",
      },
      {
        name: "Google Podcasts",
        src: "/static/podcast/google.webp",
        link: "https://podcasts.google.com/feed/aHR0cHM6Ly9mZWVkcy50cmFuc2lzdG9yLmZtL2NsaW1hdGUtY2xlYXI",
      },
      {
        name: "Spotify",
        src: "/static/podcast/spotify.webp",
        link: "https://open.spotify.com/show/09uIBGYH7dPEwWXWxasOYi",
      },
      {
        name: "Amazon Music",
        src: "/static/podcast/amazon.webp",
        link: "https://music.amazon.com/podcasts/65335518-5e3a-4da2-811a-764ae18b0fca/climate-clear",
      },
    ],
    latestEpisode:
      "Invisible, Unbreakable, Unnatural: Protecting Our Water from PFAS & Other Risks",
    latestEpisodeDesc:
      "What are PFAS and how do they affect us?  How can we protect our drinking water in the face of extreme weather events and industrial pollution? How do we make our communities more resilient? Tune in to hear Marc Yaggi, CEO of Waterkeeper Alliance and long-time defender of clean water, unravel these questions and more.",
    allEpisodesButton: "See All Episodes",
    ep: "https://media.transistor.fm/9ab06680/59f7ab67.mp3",
    rss: "http://areahub.com/podcast",
    nextSectionColor: "white",
  };
  const textHorLineStyle = {
    bg: color,
    color: "black",
    h: "1px",
    w: "60px",
    content: `""`,
    flex: "1",
    display: "inline-block",
    verticalAlign: "middle",
  };

  const sharedHeadingStyle = {
    w: "fit-content",
    mx: "auto",
    fontWeight: "semibold",
    maxW: "4xl",
  };
  // Large (desktop) and small (mobile) player height
  const [lgPlayerH, smPlayerH] = [160, 70];
  // Padding between the player and the element before it
  const playerPadding = 15;
  // The height (size) of the button that's located on top of the player
  const [lgPlayerButtonH, smPlayerButtonH] = [
    lgPlayerH * 0.55,
    smPlayerH * 1.2,
  ];
  // The amount of space this section hangs over the next section
  const [lgHangH, smHangH] = [0, 0];

  return (
    <>
      <SectionTemplate
        {...{ color, bg }}
        // Add padding to enable podcast player 'hanging' effect
        pb={{
          base: `${smPlayerH / 2 + playerPadding + 30}px`,
          md: `${lgPlayerH / 2 + playerPadding}px`,
        }}
      >
        <Box maxW="5xl" mx="auto">
          {/** Wing-Flanked Header */}
          <Heading
            {...sharedHeadingStyle}
            as="h2"
            size="md"
            _before={{ ...textHorLineStyle, mr: "15px" }}
            _after={{ ...textHorLineStyle, ml: "15px" }}
          >
            {header}
          </Heading>
          {/** Title */}
          <Heading
            {...sharedHeadingStyle}
            as="h2"
            mt="10px"
            fontWeight="bold"
            size="3xl"
          >
            {title}
          </Heading>
          {/** Subtitle */}
          <Heading
            {...sharedHeadingStyle}
            as="h3"
            mt={{ base: "20px", md: "20px" }}
            size="sm"
            textAlign="center"
            fontWeight="normal"
          >
            {subtitle}
          </Heading>
        </Box>
        <Box maxW="5xl" mx="auto" mt={{ base: "0px", md: "40px" }}>
          <Flex
            justifyContent="space-between"
            alignItems={{ base: "center" }}
            flexDir={{ base: "column", md: "row" }}
          >
            {/** Latest Episode Text */}
            <Heading
              as="h2"
              fontWeight="bold"
              size={{ base: "xl", md: "sm" }}
              mt={{ base: "30px", md: "0" }}
              whiteSpace="nowrap"
              w="fit-content"
            >
              Latest Episode:
            </Heading>
            <Text
              as="h3"
              fontWeight="normal"
              justifySelf="center"
              overflow="hidden"
              fontSize={{ base: "sm", md: "md" }}
              textAlign="center"
              mb={{ base: "10px", md: "0px" }}
              noOfLines={{ base: 2, md: 1 }}
            >
              {data.latestEpisode}
            </Text>
            {/** All Episodes Button */}
            <AllEpisodesButton
              link={data.rss}
              borderRadius="30px"
              borderWidth="1px"
              {...{ bg: "white", color }}
              size="md"
              _hover={{ bg: oneDarker(bg) }}
              display={{ base: "none", md: "block" }}
            >
              {data.allEpisodesButton}
            </AllEpisodesButton>
          </Flex>
        </Box>
        {/** Bottom Hangy Bit */}
        <Box
          mt={{ base: "0px", md: "2" }}
          position="relative"
          maxW={{ base: "lg", md: "5xl" }}
          // Center the absolute positioned div
          mx="auto"
        >
          <Box
            h={{
              base: `${smPlayerH}px`,
              md: `${lgPlayerH}px`,
            }}
            // Normal styling
            bg="white"
            borderRadius={{ base: "50px", md: "10px" }}
            borderWidth="1px"
            borderColor={{ base: "gray.200", md: "gray.200" }}
          >
            <Box position="relative" h="full">
              {/** Audio Waveform */}
              <Box
                transform="translateY(13%)"
                mx="auto"
                w="90%"
                h="80%"
                position="relative"
              >
                <Image
                  src="/static/podcast/audioForm.webp"
                  alt="Audio Waveform"
                  fill
                />
              </Box>
              {/** Play/Pause Button */}
              <AudioButton
                audioUrl={data.ep}
                // Make sure the center of the icon is white to block out the background behind
                bg="white"
                // Crop out the background around the circular icon using border radius
                borderRadius={{
                  base: `${smPlayerButtonH / 2}px`,
                  md: `${lgPlayerButtonH / 2}px`,
                }}
                borderWidth="1px"
                borderColor="gray.700"
                // Icon size + color
                boxSize={{
                  base: `${smPlayerButtonH}px`,
                  md: `${lgPlayerButtonH}px`,
                }}
                color="green.500"
                // Need absolute position to render above the image (since next/legacy/image renders absolute pos)
                position="absolute"
                // Center the icon
                left="0"
                right="0"
                mx="auto"
                // Raise the icon up a bit above the box
                top={{
                  base: `${(smPlayerH - smPlayerButtonH) / 2}px`,
                  md: `${(lgPlayerH - lgPlayerButtonH) / 2}px`,
                }}
                // Custom cursor to indicate clickable element
                cursor="pointer"
                _hover={{
                  color: "blue.800",
                }}
              />
            </Box>
            <AllEpisodesButton
              mx="auto"
              w="fit-content"
              link={data.rss}
              mt="15px"
              borderRadius="30px"
              borderWidth="1px"
              {...{ bg: "white", color }}
              _hover={{ bg: oneDarker(bg) }}
              size="lg"
              display={{ base: "block", md: "none" }}
            >
              {data.allEpisodesButton}
            </AllEpisodesButton>
          </Box>
        </Box>
      </SectionTemplate>
      <Box h={{ base: `${smHangH}px`, md: `${lgHangH}px` }} />
    </>
  );
}
